/**
 * หน้า "ออร์เดอร์ของฉัน" (?view=orders) — Glass เสมอ · สว่าง/มืดตาม oc-scheme
 * Mockup อนุมัติ: admin repo docs/superpowers/specs/2026-10-09-member-my-orders-design.md
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { AuroraBackground } from '../components/layout/AuroraBackground'
import { SchemeToggle } from '../components/layout/SchemeToggle'
import { MyOrderCard } from './MyOrderCard'
import { formatBaht, orderDeadline, splitByTab, type MemberOrdersResponse } from './myOrdersLogic'
import { useMyOrders } from './useMyOrders'
import { useOrderCusFeatures, type OrderCusFeatures } from '../lib/orderCusFeatures'
import { AddressBook } from './AddressBook'
import { claimBirthdayGift, currentMemberIdToken, type MemberProfile } from '../lib/api/memberApi'
import { MemberHub } from './hub/MemberHub'
import { AdminContact } from './AdminContact'
import { canUseMemberBenefits } from './memberEligibility'
import { BackupAddresses } from './BackupAddresses'
import { readJoinIntent, readOpenAddressIntent, stripJoinParam, type MemberWelcome } from './memberJoin'
import { WelcomeCelebration } from './WelcomeCelebration'
import { offeringUrl } from '../offering/offeringLogic'
import { TempleLatestStrip, TemplePlaysDialog } from './TemplePlaysDialog'
import { LOADER_RETRY_MS, loaderStage } from './myOrdersLoadingLogic'

const GUIDE_VIDEO = '/ordercus-guide.mp4'
const GUIDE_POSTER = '/ordercus-guide-poster.jpg'

type Tab = 'active' | 'history'
const MAX_SHOWN = 50

function Notice({ text, actionLabel, onAction }: { text: string; actionLabel: string; onAction: () => void }) {
  return (
    <div className="mo-glass mo-notice" role="status">
      <p>{text}</p>
      <button type="button" className="mo-notice-btn" onClick={onAction}>
        {actionLabel}
      </button>
    </div>
  )
}

/**
 * ตัวโหลดหน้าสมาชิก — บอกขั้นตอน + โครงเหมือนหน้าจริง + ทางออกเมื่อช้า (เดิมเป็นกล่องเทากะพริบเฉย ๆ ดูเหมือนค้าง)
 * ขั้น/ข้อความดู myOrdersLoadingLogic · นับเวลาในเครื่อง ไม่ยิง API เพิ่ม
 */
function MemberLoader({ featuresReady, onRetry }: { featuresReady: boolean; onRetry: () => void }) {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const started = Date.now()
    const timer = window.setInterval(() => {
      const ms = Date.now() - started
      setElapsed(ms)
      if (ms >= LOADER_RETRY_MS) window.clearInterval(timer)
    }, 1000)
    return () => window.clearInterval(timer)
  }, [])
  const st = loaderStage(elapsed, featuresReady)
  return (
    <div className="mo-loader" role="status" aria-live="polite" aria-label={st.title}>
      <div className="mo-loader-bar" aria-hidden="true"><i style={{ width: `${st.pct}%` }} /></div>
      <div className="mo-glass mo-loader-status">
        <span className="mo-loader-orb" aria-hidden="true"><b /></span>
        <div className="mo-loader-txt">
          <strong>{st.title}</strong>
          <span>{st.sub}</span>
          <span className="mo-loader-steps" aria-hidden="true">
            {[1, 2, 3].map((n) => <i key={n} className={n <= st.step ? 'on' : ''} />)}
          </span>
          {st.retry && (
            <span className="mo-loader-acts">
              <button type="button" className="mo-notice-btn" onClick={onRetry}>ลองใหม่</button>
              <button type="button" className="mo-loader-link" onClick={() => document.getElementById('member-contact')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>ติดต่อแอดมิน</button>
            </span>
          )}
        </div>
      </div>
      <div className="mo-glass mo-card mo-skel mo-skeleton" aria-hidden="true">
        <div className="mo-skel-row"><span className="mo-skel-av" /><span className="mo-skel-grow"><i style={{ width: '52%' }} /><i style={{ width: '34%' }} /></span><span className="mo-skel-chip" /></div>
        <div className="mo-skel-stats"><i /><i /><i /></div>
      </div>
      {[0, 1].map((n) => (
        <div key={n} className="mo-glass mo-card mo-skel mo-skeleton" aria-hidden="true">
          <div className="mo-skel-row"><span className="mo-skel-th" /><span className="mo-skel-grow"><i style={{ width: '70%' }} /><i style={{ width: '46%' }} /><i style={{ width: '30%' }} /></span><span className="mo-skel-chip mo-skel-chip--sm" /></div>
        </div>
      ))}
    </div>
  )
}

function Profile({ data, showLifetimeTotal, onEdit }: { data: MemberOrdersResponse; showLifetimeTotal: boolean; onEdit: () => void }) {
  const savedName = String(data.memberProfile?.recipientName || '').trim()
  const name = data.profile?.displayName || savedName || (data.viewAs ? 'ลูกค้า' : 'สมาชิก')
  const greetingName = savedName || name
  const pic = data.profile?.pictureUrl || ''
  const tier = !data.viewAs ? data.membership?.tier : null
  const tierColor = tier && /^#[0-9a-f]{6}$/i.test(tier.color) ? tier.color : '#dfba6b'
  const points = data.membership?.points
  const toNext = data.membership?.toNextTierPoints
  // ความคืบหน้าไประดับถัดไป: คำนวณจากแต้มที่มี/แต้มรวมที่ต้องถึง (ไม่มีข้อมูลแต้ม = ไม่แสดงแถบ)
  const progress = points !== undefined && toNext !== undefined && data.membership?.nextTier ? Math.max(0, Math.min(100, Math.round((points / (points + toNext)) * 100))) : null
  return (
    <>
      <div className="mo-greet">
        {pic ? (
          <img className="mo-avatar" src={pic} alt="" />
        ) : (
          <span className="mo-avatar" aria-hidden="true">
            {name.slice(0, 1)}
          </span>
        )}
        <div className="mo-who">
          <div className="mo-name">{name}</div>
          {data.viewAs ? <div className="mo-sub">{`ออร์เดอร์ #${data.viewAs.orderId}`}</div> : <div className="mo-greeting">สวัสดี {greetingName} ขอให้เป็นวันที่ดีครับ</div>}
        </div>
      </div>
      <section id="member-profile" className="mo-glass mo-rim mo-profile" aria-label="ข้อมูลสมาชิก" style={{ '--mo-tier-accent': tierColor } as CSSProperties}>
        {!data.viewAs && <div className="mo-profile-tier">
          <span className="mo-profile-tier-mark" aria-hidden="true"><i className="fas fa-crown" /></span>
          <div className="mo-profile-tier-copy">
            <small>ระดับสมาชิกของคุณ</small>
            <strong>{tier?.name || 'สมาชิก LINE'}</strong>
          </div>
          <button type="button" className="mo-profile-edit" onClick={onEdit} aria-label="ดูหรือแก้ไขข้อมูลส่วนตัว"><i className="fas fa-pen" aria-hidden="true" /><span>ข้อมูลส่วนตัว</span></button>
        </div>}
        <div className="mo-stat">
          <div className="mo-stat-v">{data.viewAs ? (showLifetimeTotal ? formatBaht(data.summary?.lifetimeTotal || 0) : '—') : (points?.toLocaleString('th-TH') ?? '—')}</div>
          <div className="mo-stat-l">{data.viewAs ? 'ยอดบูชาสะสม' : 'คะแนนสะสม'}</div>
        </div>
        {!data.viewAs && data.membership?.nextTier && <div className="mo-progress">
          {progress !== null && <div className="mo-progress-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} aria-label={`ความคืบหน้าไประดับ ${data.membership.nextTier.name}`}><i style={{ width: `${progress}%` }} /></div>}
          <span>{toNext !== undefined ? `อีก ${toNext.toLocaleString('th-TH')} แต้ม` : `อีก ฿${data.membership.toNextTierBaht.toLocaleString('th-TH')}`} ถึง {data.membership.nextTier.name}</span>
        </div>}
      </section>
    </>
  )
}

function MemberBenefits({ data, showLifetimeTotal, onProfile }: { data: MemberOrdersResponse; showLifetimeTotal: boolean; onProfile: () => void }) {
  const [claimStatus, setClaimStatus] = useState(data.birthdayOffer?.claimStatus || null)
  const [claimBusy, setClaimBusy] = useState(false)
  const [claimError, setClaimError] = useState('')
  useEffect(() => { setClaimStatus(data.birthdayOffer?.claimStatus || null) }, [data.birthdayOffer?.claimStatus])
  const gift = data.birthdayOffer
  const claim = async () => {
    setClaimBusy(true)
    setClaimError('')
    try {
      const token = await currentMemberIdToken()
      if (!token) throw new Error('กรุณาเข้าสู่ระบบ LINE ใหม่')
      const response = await claimBirthdayGift(token)
      if (!response.data.ok) throw new Error(response.data.message || 'รับสิทธิ์ไม่สำเร็จ')
      setClaimStatus(response.data.status || 'pending')
    } catch (error) {
      setClaimError((error as Error).message)
    } finally {
      setClaimBusy(false)
    }
  }
  if (!canUseMemberBenefits(data)) return <section className="mo-glass mo-benefits mo-benefits-locked" aria-label="สิทธิ์สมาชิก">
    <i className="fas fa-lock" aria-hidden="true" />
    <h2 className="mo-benefits-title">ยืนยันข้อมูลเพื่อเปิดรับสิทธิพิเศษ</h2>
    <p className="mo-benefits-empty">กรอกข้อมูลปัจจุบันให้ครบก่อนรับของขวัญ เลขมงคล และร่วมกิจกรรมสมาชิก</p>
    <button type="button" onClick={onProfile}>ยืนยันข้อมูลสมาชิก</button>
  </section>
  return <section className="mo-glass mo-benefits" aria-label="สิทธิ์สมาชิก">
    <h2 className="mo-benefits-title">สิทธิ์พิเศษของคุณ</h2>
    {showLifetimeTotal && <div className="mo-benefits-offer"><b>ยอดบูชาสะสม {formatBaht(data.summary?.lifetimeTotal || 0)}</b><p>สะสมจาก {data.summary?.totalOrders || 0} ออร์เดอร์</p></div>}
    {gift && <div className="mo-benefits-offer">
      <b><i className="fas fa-gift" aria-hidden="true" /> {gift.title}</b>
      {gift.description && <p>{gift.description}</p>}
      {claimStatus ? <small>{claimStatus === 'fulfilled' ? 'มอบของขวัญแล้ว' : 'รับสิทธิ์แล้ว · รอทีมงานดำเนินการ'}</small>
        : gift.eligible ? <button type="button" disabled={claimBusy} onClick={() => void claim()}>{claimBusy ? 'กำลังบันทึก…' : 'รับสิทธิ์ของขวัญวันเกิด'}</button>
          : <small>{gift.needsBirthday ? 'กรอกวันเกิดในข้อมูลพื้นฐานเพื่อรับสิทธิ์' : 'รับสิทธิ์ได้ในเดือนเกิดของคุณ'}</small>}
      {claimError && <p role="alert" className="mo-benefits-error">{claimError}</p>}
    </div>}
    {data.luckyCampaign && <div className="mo-benefits-offer">
      <b><i className="fas fa-star" aria-hidden="true" /> {data.luckyCampaign.title}</b>
      <p>{data.luckyCampaign.message}</p>
    </div>}
    {!gift && !data.luckyCampaign && !showLifetimeTotal && <p className="mo-benefits-empty">สิทธิพิเศษใหม่จะปรากฏที่นี่เมื่อเปิดแคมเปญ</p>}
  </section>
}

function MemberGuide({ open, onClose }: { open: boolean; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { onClose(); return }
      if (event.key !== 'Tab') return
      const controls = panelRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), video[controls]')
      if (!controls?.length) return
      if (event.shiftKey && document.activeElement === controls[0]) { event.preventDefault(); controls[controls.length - 1].focus() }
      else if (!event.shiftKey && document.activeElement === controls[controls.length - 1]) { event.preventDefault(); controls[0].focus() }
    }
    closeRef.current?.focus()
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])
  if (!open) return null
  return createPortal(<div className="mo-guide-backdrop" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <section ref={panelRef} className="mo-guide-dialog" role="dialog" aria-modal="true" aria-labelledby="member-guide-title">
        <button ref={closeRef} type="button" className="mo-guide-close" onClick={onClose} aria-label="ปิดหน้าต่าง">×</button>
        <h2 id="member-guide-title">วิธีชำระบูชา</h2>
        <p>วิดีโอแนะนำการยืนยันออร์เดอร์บูชา</p>
        <video controls playsInline preload="none" poster={GUIDE_POSTER} aria-label="วิดีโอแนะนำการใช้งาน OrderCus">
          <source src={GUIDE_VIDEO} type="video/mp4" />
          เบราว์เซอร์นี้ไม่รองรับการเล่นวิดีโอ
        </video>
      </section>
    </div>, document.body)
}

function MemberMenu({ onProfile, onBenefits, onOrders, onTemple, onOffering, onActivities, onGuide, onContact }: {
  onProfile: () => void; onBenefits: () => void; onOrders: () => void; onTemple: () => void; onOffering: () => void; onActivities: () => void; onGuide: () => void; onContact: () => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const firstRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); triggerRef.current?.focus() }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    firstRef.current?.focus()
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])
  const select = (action: () => void) => { setOpen(false); action() }
  return <nav ref={rootRef} className="mo-menu-control" aria-label="เมนูสมาชิก">
    <button ref={triggerRef} type="button" className="mo-menu-button" aria-expanded={open} aria-controls="member-menu-panel" onClick={() => setOpen(!open)}>
      <i className={`fas ${open ? 'fa-xmark' : 'fa-bars'}`} aria-hidden="true" /><span>เมนู</span>
    </button>
    {open && <div id="member-menu-panel" className="mo-menu-popover">
      <div className="mo-menu-heading">ศูนย์สมาชิก<small>เลือกสิ่งที่ต้องการดู</small></div>
      <div className="mo-menu-list">
        <button ref={firstRef} type="button" onClick={() => select(onProfile)}><i className="fas fa-id-card" aria-hidden="true" />ข้อมูลส่วนตัว</button>
        <button type="button" onClick={() => select(onBenefits)}><i className="fas fa-crown" aria-hidden="true" />คะแนนและสิทธิพิเศษ</button>
        <button type="button" onClick={() => select(onOrders)}><i className="fas fa-receipt" aria-hidden="true" />ออร์เดอร์ของฉัน</button>
        <button type="button" onClick={() => select(onTemple)}><i className="fas fa-fire-flame-curved" aria-hidden="true" />เสี่ยงโชคและการ์ดมงคล</button>
        <button type="button" onClick={() => select(onOffering)}><i className="fas fa-hands-praying" aria-hidden="true" />ร่วมถวายทรัพย์</button>
        <button type="button" onClick={() => select(onActivities)}><i className="fas fa-gift" aria-hidden="true" />กิจกรรมในถุงทอง</button>
        <button type="button" onClick={() => select(onGuide)}><i className="fas fa-circle-play" aria-hidden="true" />วิธีชำระบูชา</button>
        <button type="button" onClick={() => select(onContact)}><i className="fas fa-comments" aria-hidden="true" />ติดต่อแอดมิน</button>
      </div>
    </div>}
  </nav>
}

function BenefitsDialog({ data, showLifetimeTotal, onProfile, onClose }: { data: MemberOrdersResponse; showLifetimeTotal: boolean; onProfile: () => void; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key !== 'Tab') return
      const controls = panelRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]')
      if (!controls?.length) return
      if (event.shiftKey && document.activeElement === controls[0]) { event.preventDefault(); controls[controls.length - 1].focus() }
      else if (!event.shiftKey && document.activeElement === controls[controls.length - 1]) { event.preventDefault(); controls[0].focus() }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', onKeyDown) }
  }, [onClose])
  return createPortal(<div className="mo-benefits-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
    <section ref={panelRef} className="mo-benefits-dialog" role="dialog" aria-modal="true" aria-label="คะแนนและสิทธิพิเศษ">
      <div className="mo-benefits-head"><span>คะแนนและสิทธิพิเศษ</span><button ref={closeRef} type="button" onClick={onClose} aria-label="ปิด"><i className="fas fa-xmark" aria-hidden="true" /></button></div>
      <MemberBenefits data={data} showLifetimeTotal={showLifetimeTotal} onProfile={() => { onClose(); onProfile() }} />
    </section>
  </div>, document.body)
}

function AdminBanner({ data }: { data: MemberOrdersResponse }) {
  if (!data.viewAs) return null
  return (
    <div className="mo-admin-banner" role="note">
      <span aria-hidden="true">👁</span>
      <span>
        มุมมองลูกค้า: <b>{data.viewAs.displayName || 'ลูกค้า'}</b> · โหมดแอดมิน (อ่านอย่างเดียว)
      </span>
    </div>
  )
}

function Ready({ data, onClose, features, onProfileSaved, botId, profileRequest, activityRequest, benefitsOpen, onProfileRequest, onActivitiesOpen, onBenefitsClose, joinMode, onWelcome, onTempleOpen }: {
  data: MemberOrdersResponse; onClose: () => void; features: OrderCusFeatures; onProfileSaved: (profile: MemberProfile) => void; botId: string
  profileRequest: number; activityRequest: number; benefitsOpen: boolean; onProfileRequest: () => void; onActivitiesOpen: () => void; onBenefitsClose: () => void
  joinMode: boolean; onWelcome: (welcome: MemberWelcome) => void
  onTempleOpen?: () => void
}) {
  const [tab, setTab] = useState<Tab>('active')
  const tabsRef = useRef<HTMLDivElement>(null)
  const [now, setNow] = useState(Date.now)
  const hasDeadline = data.orders?.some((order) => !!orderDeadline(order)) ?? false
  useEffect(() => {
    if (!hasDeadline) return
    const update = () => { if (!document.hidden) setNow(Date.now()) }
    update()
    const timer = window.setInterval(update, 60_000)
    document.addEventListener('visibilitychange', update)
    window.addEventListener('focus', update)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', update)
      window.removeEventListener('focus', update)
    }
  }, [hasDeadline])
  const orders = data.orders || []
  const readOnly = !!data.viewAs
  const benefitsEligible = canUseMemberBenefits(data)
  const { active, history } = splitByTab(orders, now)
  const list = tab === 'active' ? active : history
  // ป้ายประกาศ + ถุงทอง — ไม่แสดงในโหมดแอดมินดูมุมมองลูกค้า
  const hub = readOnly ? null : (
    <MemberHub
      announcements={data.announcements}
      activities={data.activities}
      hasNew={!!data.activitiesHasNew}
      benefitsEligible={benefitsEligible}
      openRequest={activityRequest}
      botId={botId}
      onGoOrders={() => {
        setTab('active')
        tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }}
      onProfileRequest={onProfileRequest}
      onActivitiesOpen={onActivitiesOpen}
    />
  )

  if (!orders.length) {
    return (
      <>
        <AdminBanner data={data} />
        <Profile data={data} showLifetimeTotal={features.lifetimeTotal} onEdit={onProfileRequest} />
        {!readOnly && <AddressBook initialProfile={data.memberProfile} onSaved={onProfileSaved} openRequest={profileRequest} joinMode={joinMode} onWelcome={onWelcome} hasOrders={(data.summary?.totalOrders || 0) > 0 || (data.orders?.length || 0) > 0} />}
        {!readOnly && features.savedAddresses && <BackupAddresses defaults={data.memberProfile ? { recipientName: data.memberProfile.recipientName, phone: data.memberProfile.phone } : undefined} />}
        <div id="member-orders"><Notice text="ยังไม่มีรายการบูชา" actionLabel="กลับไปที่แชท" onAction={onClose} /></div>
        {!readOnly && benefitsOpen && <BenefitsDialog data={data} showLifetimeTotal={features.lifetimeTotal} onProfile={onProfileRequest} onClose={onBenefitsClose} />}
        {hub}
      </>
    )
  }

  return (
    <>
      <AdminBanner data={data} />
      <Profile data={data} showLifetimeTotal={features.lifetimeTotal} onEdit={onProfileRequest} />
      {!readOnly && <AddressBook initialProfile={data.memberProfile} onSaved={onProfileSaved} openRequest={profileRequest} joinMode={joinMode} onWelcome={onWelcome} hasOrders={(data.summary?.totalOrders || 0) > 0 || (data.orders?.length || 0) > 0} />}
      {!readOnly && features.savedAddresses && <BackupAddresses defaults={data.memberProfile ? { recipientName: data.memberProfile.recipientName, phone: data.memberProfile.phone } : undefined} />}
      {!readOnly && data.templeLatest && onTempleOpen && <TempleLatestStrip latest={data.templeLatest} onOpen={onTempleOpen} />}
      <div id="member-orders" ref={tabsRef} className="mo-glass mo-tabs" role="tablist" aria-label="ออร์เดอร์">
        <button type="button" role="tab" aria-selected={tab === 'active'} className={tab === 'active' ? 'is-on' : ''} onClick={() => setTab('active')}>
          กำลังดำเนินการ<span className="mo-count">{active.length}</span>
        </button>
        <button type="button" role="tab" aria-selected={tab === 'history'} className={tab === 'history' ? 'is-on' : ''} onClick={() => setTab('history')}>
          ประวัติ<span className="mo-count">{history.length}</span>
        </button>
      </div>
      <div role="tabpanel">
        {list.length ? (
          list.map((o) => <MyOrderCard key={o.orderId} order={o} readOnly={readOnly} showTracking={features.tracking} now={now} />)
        ) : (
          <div className="mo-empty-tab">
            <p>{tab === 'active' ? 'ตอนนี้ไม่มีออร์เดอร์ที่กำลังดำเนินการ' : 'ยังไม่มีประวัติ'}</p>
            {tab === 'active' && history.length > 0 && <button type="button" className="mo-empty-link" onClick={() => setTab('history')}>ดูประวัติ {history.length} รายการ</button>}
          </div>
        )}
        {tab === 'history' && orders.length >= MAX_SHOWN && <p className="mo-more">แสดงล่าสุด {MAX_SHOWN} ใบ</p>}
      </div>
      {!readOnly && benefitsOpen && <BenefitsDialog data={data} showLifetimeTotal={features.lifetimeTotal} onProfile={onProfileRequest} onClose={onBenefitsClose} />}
      {hub}
    </>
  )
}

export default function MyOrdersPage() {
  const { state, botId, asOrderId, reload, refreshActivities, close, logout, updateMemberProfile } = useMyOrders()
  const [profileRequest, setProfileRequest] = useState(() => (!asOrderId && readOpenAddressIntent(window.location.search) ? 1 : 0))
  const [activityRequest, setActivityRequest] = useState(0)
  const [benefitsOpen, setBenefitsOpen] = useState(false)
  const [guideOpen, setGuideOpen] = useState(false)
  const [templeOpen, setTempleOpen] = useState(false)
  // ลิงก์สมัครจากการ์ดชวนสมัคร (join=1) · พิธีต้อนรับอยู่ระดับหน้า (reload รายการไม่ทำให้หาย)
  const [joinMode] = useState(() => !asOrderId && readJoinIntent(window.location.search))
  const [welcome, setWelcome] = useState<MemberWelcome | null>(null)
  const onWelcome = (value: MemberWelcome) => {
    setWelcome(value)
    try { window.history.replaceState(null, '', stripJoinParam(window.location.href)) } catch { /* ไม่เป็นไร */ }
  }
  // หน้า member ใช้ LIFF + memberApi; ไม่ควรเปิด Firebase polling เพียงเพื่ออ่าน feature flags
  const { features, ready } = useOrderCusFeatures({ remote: false })
  return (
    <>
      <AuroraBackground />
      <div className="mo-scene mo-scene--member" aria-hidden="true"><img src="/temple-loop-poster.jpg" alt="" decoding="async" /></div>
      <main className="mo-page" aria-busy={state.phase === 'loading'}>
        <header className="mo-top">
          {asOrderId ? <h1 className="mo-title">ออร์เดอร์ของลูกค้า</h1> : <div className="mo-member-brand"><h1 className="mo-title">ศรีคเนศ เทวาลัย</h1></div>}
          {!asOrderId && state.phase === 'ready' && <MemberMenu
            onProfile={() => setProfileRequest((value) => value + 1)}
            onBenefits={() => setBenefitsOpen(true)}
            onOrders={() => document.getElementById('member-orders')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            onTemple={() => setTempleOpen(true)}
            onOffering={() => window.location.assign(offeringUrl(botId || '1'))}
            onActivities={() => setActivityRequest((value) => value + 1)}
            onGuide={() => setGuideOpen(true)}
            onContact={() => document.getElementById('member-contact')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
          />}
          <div className="mo-top-actions">
            <div className="mo-logout-control">
              <button type="button" className="mo-logout-button" onClick={() => { void logout() }} aria-label="ออกจากระบบ" title="ออกจากระบบ">
                <i className="fas fa-right-from-bracket" aria-hidden="true" />
              </button>
              <span className="mo-logout-tip" aria-hidden="true">กดปุ่มนี้เพื่อออกจากระบบ</span>
            </div>
            <SchemeToggle />
          </div>
        </header>
        {!ready && <MemberLoader featuresReady={false} onRetry={reload} />}
        {ready && !features.myOrders && <Notice text="หน้าออร์เดอร์ของฉันปิดใช้งานชั่วคราว กรุณาติดต่อแอดมิน" actionLabel="กลับไปที่แชท" onAction={close} />}
        {ready && features.myOrders && state.phase === 'loading' && <MemberLoader featuresReady onRetry={reload} />}
        {ready && features.myOrders && state.phase === 'auth' && <Notice text={state.message} actionLabel="ปิดหน้านี้" onAction={close} />}
        {ready && features.myOrders && state.phase === 'error' && <Notice text={state.message} actionLabel="ลองใหม่" onAction={reload} />}
        {ready && features.myOrders && state.phase === 'ready' && <Ready data={state.data} onClose={close} features={features} onProfileSaved={(profile) => { updateMemberProfile(profile); reload() }} botId={botId} profileRequest={profileRequest} activityRequest={activityRequest} benefitsOpen={benefitsOpen} onProfileRequest={() => setProfileRequest((value) => value + 1)} onActivitiesOpen={refreshActivities} onBenefitsClose={() => { setBenefitsOpen(false); document.querySelector<HTMLButtonElement>('.mo-menu-button')?.focus() }} joinMode={joinMode} onWelcome={onWelcome} onTempleOpen={asOrderId ? undefined : () => setTempleOpen(true)} />}
        {templeOpen && !asOrderId && <TemplePlaysDialog botId={botId || '1'} onClose={() => { setTempleOpen(false); document.querySelector<HTMLButtonElement>('.mo-menu-button')?.focus() }} />}
        {welcome && <WelcomeCelebration welcome={welcome} fallbackName={state.phase === 'ready' ? state.data.profile?.displayName : ''} onClose={() => setWelcome(null)} />}
        {!asOrderId && <footer className="mo-member-footer">
          <div id="member-contact"><AdminContact /></div>
        </footer>}
        <MemberGuide open={guideOpen} onClose={() => { setGuideOpen(false); document.querySelector<HTMLButtonElement>('.mo-menu-button')?.focus() }} />
      </main>
    </>
  )
}
